(() => {
  const STORAGE_KEY = 'ac_growing_zone_v2';
  const LEGACY_STORAGE_KEY = 'ac_growing_zone_v1';

  let lookupPromise = null;
  let activeProductRequest = null;
  let resolvingZip = null;

  const $ = (selector, root = document) => root.querySelector(selector);
  const $$ = (selector, root = document) => Array.from(root.querySelectorAll(selector));

  function normalizeZip(value) {
    const match = String(value || '').match(/\d{5}/);
    return match ? match[0] : '';
  }

  function majorZone(zone) {
    const match = String(zone || '').match(/\d+/);
    return match ? parseInt(match[0], 10) : null;
  }

  function getSavedZone() {
    for (const key of [STORAGE_KEY, LEGACY_STORAGE_KEY]) {
      try {
        const raw = localStorage.getItem(key);
        if (!raw) continue;
        const parsed = JSON.parse(raw);
        if (parsed && parsed.zone) return parsed;
      } catch (e) {}
    }
    return null;
  }

  function saveZone(state) {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
      localStorage.removeItem(LEGACY_STORAGE_KEY);
    } catch (e) {}
  }

  function clearSavedZone() {
    try {
      localStorage.removeItem(STORAGE_KEY);
      localStorage.removeItem(LEGACY_STORAGE_KEY);
    } catch (e) {}
  }

  function loadLookup() {
    if (lookupPromise) return lookupPromise;

    const finder = $('[data-ac-zone-finder]');
    const url = finder && finder.dataset.acZoneDataUrl;
    if (!url) return Promise.reject(new Error('Zone data asset not found.'));

    lookupPromise = fetch(url, { credentials: 'same-origin' })
      .then((response) => {
        if (!response.ok) throw new Error('Unable to load zone data.');
        return response.json();
      });

    return lookupPromise;
  }

  async function loadFrostData(zip) {
    const finder = $('[data-ac-zone-finder]');
    const base = finder && finder.dataset.acFrostApiBase;
    if (!base || !zip) return null;

    try {
      const response = await fetch(`${base}${encodeURIComponent(zip)}`, {
        method: 'GET',
        mode: 'cors',
        headers: { 'Accept': 'application/json' }
      });

      if (!response.ok) return null;

      const payload = await response.json();
      const data = payload && payload.data;
      if (!data) return null;

      const dates = data.frost_dates || {};
      const location = data.location || {};
      const station = data.weather_station || {};

      return {
        frostFree: Boolean(data.frost_free),
        lastFrost: dates.last_frost_32f && dates.last_frost_32f['50%'] || '',
        firstFrost: dates.first_frost_32f && dates.first_frost_32f['50%'] || '',
        location: {
          city: location.city || '',
          state: location.state || ''
        },
        station: {
          id: station.station_id || '',
          name: station.name || '',
          distanceKm: station.distance_km || null
        },
        source: 'NOAA Climate Normals 1991-2020'
      };
    } catch (error) {
      console.warn('[AC Zone] Frost lookup unavailable; keeping USDA zone only.', error);
      return null;
    }
  }

  function dateFromMMDD(mmdd, year) {
    if (!mmdd || !/^\d{2}\/\d{2}$/.test(mmdd)) return null;
    const [month, day] = mmdd.split('/').map(Number);
    const date = new Date(year, month - 1, day, 12, 0, 0, 0);
    return Number.isNaN(date.getTime()) ? null : date;
  }

  function daysBetween(from, to) {
    const ms = to.getTime() - from.getTime();
    return Math.ceil(ms / 86400000);
  }

  function formatFrostDate(mmdd) {
    const date = dateFromMMDD(mmdd, 2024);
    if (!date) return '';
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  }

  function plantingPhase(state) {
    const major = majorZone(state && state.zone);
    const zoneUrl = major ? `/collections/zone-${major}` : '/collections/all';

    if (!state || !state.frost) {
      return {
        key: 'zone-only',
        label: 'Zone-based picks',
        detail: 'Add a ZIP for local frost timing and a more useful planting window.',
        link: zoneUrl,
        linkLabel: major ? `See What Grows Best in Zone ${major}` : 'See What Grows Best'
      };
    }

    if (state.frost.frostFree) {
      return {
        key: 'frost-free',
        label: 'Long growing season',
        detail: 'A typical 32°F frost date is not reported for the nearest climate station.',
        link: zoneUrl,
        linkLabel: major ? `See What Grows Best in Zone ${major}` : 'See What Grows Best'
      };
    }

    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 12);
    const last = dateFromMMDD(state.frost.lastFrost, today.getFullYear());
    const first = dateFromMMDD(state.frost.firstFrost, today.getFullYear());

    if (!last || !first) {
      return {
        key: 'zone-only',
        label: 'Zone-based picks',
        detail: 'Your zone is ready; local frost timing was not available for this ZIP.',
        link: zoneUrl,
        linkLabel: major ? `See What Grows Best in Zone ${major}` : 'See What Grows Best'
      };
    }

    if (today < last) {
      const untilLast = daysBetween(today, last);

      if (untilLast > 56) {
        return {
          key: 'spring-planning',
          label: 'Spring planning window',
          detail: `${untilLast} days until the typical last 32°F frost.`,
          link: zoneUrl,
          linkLabel: major ? `Plan for Zone ${major}` : 'Plan Your Garden'
        };
      }

      if (untilLast > 21) {
        return {
          key: 'indoor-starting',
          label: 'Indoor-starting window',
          detail: `${untilLast} days until the typical last 32°F frost.`,
          link: zoneUrl,
          linkLabel: major ? `See What Grows Best in Zone ${major}` : 'See What Grows Best'
        };
      }

      return {
        key: 'spring-window',
        label: 'Spring planting window',
        detail: `Typical last 32°F frost is ${formatFrostDate(state.frost.lastFrost)}.`,
        link: zoneUrl,
        linkLabel: major ? `See What Grows Best in Zone ${major}` : 'See What Grows Best'
      };
    }

    if (today <= first) {
      const untilFirst = daysBetween(today, first);
      const sinceLast = daysBetween(last, today);

      if (untilFirst <= 90) {
        return {
          key: 'fall-window',
          label: 'Fall planting window',
          detail: `${untilFirst} days until the typical first 32°F frost — a useful window for quick and cool-season crops.`,
          link: '/collections/fall-collection',
          linkLabel: 'Shop Fall Seeds'
        };
      }

      if (sinceLast <= 42) {
        return {
          key: 'main-spring',
          label: 'Main planting window',
          detail: `The typical last frost has passed; ${untilFirst} days remain until the typical first frost.`,
          link: zoneUrl,
          linkLabel: major ? `See What Grows Best in Zone ${major}` : 'See What Grows Best'
        };
      }

      return {
        key: 'growing-season',
        label: 'Growing season underway',
        detail: `${untilFirst} days until the typical first 32°F frost.`,
        link: zoneUrl,
        linkLabel: major ? `See What Grows Best in Zone ${major}` : 'See What Grows Best'
      };
    }

    const nextLast = dateFromMMDD(state.frost.lastFrost, today.getFullYear() + 1);
    const untilNextLast = nextLast ? daysBetween(today, nextLast) : null;

    return {
      key: 'cool-season',
      label: 'Cool-season planning',
      detail: untilNextLast
        ? `${untilNextLast} days until next year's typical last 32°F frost.`
        : 'Use your frost dates to plan the next planting window.',
      link: zoneUrl,
      linkLabel: major ? `Plan for Zone ${major}` : 'Plan Your Garden'
    };
  }

  function displayLocation(state) {
    const frostLoc = state && state.frost && state.frost.location;
    const city = frostLoc && frostLoc.city || state && state.city || '';
    const region = frostLoc && frostLoc.state || state && state.state || '';

    if (city && region) return `${city}, ${region}`;
    if (city) return city;
    if (state && state.zip) return `ZIP ${state.zip}`;
    return 'Your garden';
  }

  function setFinderMessage(state) {
    $$('[data-ac-zone-finder]').forEach((finder) => {
      const result = $('[data-ac-zone-result]', finder);
      const error = $('[data-ac-zone-error]', finder);
      const input = $('[data-ac-zone-zip]', finder);

      if (error) {
        error.hidden = true;
        error.textContent = '';
      }

      if (!state || !result) return;

      result.hidden = false;

      const loc = $('[data-ac-result-location]', result);
      const zone = $('[data-ac-result-zone]', result);
      const last = $('[data-ac-result-last-frost]', result);
      const first = $('[data-ac-result-first-frost]', result);
      const lastWrap = $('[data-ac-last-frost-wrap]', result);
      const firstWrap = $('[data-ac-first-frost-wrap]', result);
      const plantingWrap = $('[data-ac-planting-wrap]', result);
      const planting = $('[data-ac-result-planting]', result);
      const detail = $('[data-ac-result-planting-detail]', result);
      const plantingLink = $('[data-ac-planting-link]', result);

      if (loc) loc.textContent = displayLocation(state);
      if (zone) zone.textContent = `Zone ${state.zone}`;

      if (state.frost && state.frost.frostFree) {
        if (last) last.textContent = 'Frost uncommon';
        if (first) first.textContent = 'Frost uncommon';
        if (lastWrap) lastWrap.hidden = false;
        if (firstWrap) firstWrap.hidden = false;
      } else if (state.frost && (state.frost.lastFrost || state.frost.firstFrost)) {
        if (last) last.textContent = formatFrostDate(state.frost.lastFrost) || '—';
        if (first) first.textContent = formatFrostDate(state.frost.firstFrost) || '—';
        if (lastWrap) lastWrap.hidden = false;
        if (firstWrap) firstWrap.hidden = false;
      } else {
        if (lastWrap) lastWrap.hidden = true;
        if (firstWrap) firstWrap.hidden = true;
      }

      const phase = plantingPhase(state);
      if (plantingWrap) plantingWrap.hidden = false;
      if (planting) planting.textContent = phase.label;
      if (detail) detail.textContent = phase.detail;
      if (plantingLink) {
        plantingLink.href = phase.link;
        plantingLink.textContent = phase.linkLabel;
      }

      if (input && !input.value) input.value = state.zip || '';
    });
  }

  function showFinderError(finder, message) {
    const result = $('[data-ac-zone-result]', finder);
    const error = $('[data-ac-zone-error]', finder);
    if (result) result.hidden = true;
    if (error) {
      error.textContent = message;
      error.hidden = false;
    }
  }

  const STATE_NAME_TO_CODE = {
    'alabama':'AL','alaska':'AK','arizona':'AZ','arkansas':'AR','california':'CA','colorado':'CO','connecticut':'CT','delaware':'DE','florida':'FL','georgia':'GA','hawaii':'HI','idaho':'ID','illinois':'IL','indiana':'IN','iowa':'IA','kansas':'KS','kentucky':'KY','louisiana':'LA','maine':'ME','maryland':'MD','massachusetts':'MA','michigan':'MI','minnesota':'MN','mississippi':'MS','missouri':'MO','montana':'MT','nebraska':'NE','nevada':'NV','new hampshire':'NH','new jersey':'NJ','new mexico':'NM','new york':'NY','north carolina':'NC','north dakota':'ND','ohio':'OH','oklahoma':'OK','oregon':'OR','pennsylvania':'PA','rhode island':'RI','south carolina':'SC','south dakota':'SD','tennessee':'TN','texas':'TX','utah':'UT','vermont':'VT','virginia':'VA','washington':'WA','west virginia':'WV','wisconsin':'WI','wyoming':'WY','district of columbia':'DC'
  };

  function stateCode(value) {
    const raw = String(value || '').trim();
    if (/^[A-Za-z]{2}$/.test(raw)) return raw.toUpperCase();
    return STATE_NAME_TO_CODE[raw.toLowerCase()] || '';
  }

  function updateMapMarker(state) {
    $$('[data-ac-zone-map]').forEach((map) => {
      const svg = $('[data-ac-zone-map-svg]', map);
      const marker = $('[data-ac-zone-map-marker]', map);
      if (!svg || !marker) return;

      const code = stateCode(state && (state.state || state.frost && state.frost.location && state.frost.location.state));
      if (!code) { marker.hidden = true; return; }

      const shape = svg.querySelector('.' + code.toLowerCase());
      if (!shape || !shape.getBBox) { marker.hidden = true; return; }

      try {
        const box = shape.getBBox();
        const x = box.x + box.width / 2;
        const y = box.y + box.height / 2;
        marker.setAttribute('transform', `translate(${x} ${y})`);
        marker.hidden = false;
      } catch (e) { marker.hidden = true; }
    });
  }

  function updateMapPicks() {
    const names = [];
    const popular = $('[data-ac-popular-picks]');
    if (popular) {
      $$('.ac-product-card__title a', popular).slice(0, 3).forEach((node) => {
        let name = (node.textContent || '').trim();
        name = name.replace(/\s+Seeds$/i, '').replace(/\s+Seed$/i, '');
        if (name) names.push(name);
      });
    }
    $$('[data-ac-map-picks]').forEach((node) => {
      node.textContent = names.length ? names.join(', ') : 'zone-matched favorites';
    });
  }

  function updateMapCard(state) {
    $$('[data-ac-zone-map]').forEach((map) => {
      const location = $('[data-ac-map-location]', map);
      const zone = $('[data-ac-map-zone]', map);
      const frost = $('[data-ac-map-frost]', map);
      if (!state) return;
      if (location) location.textContent = displayLocation(state);
      if (zone) zone.textContent = `Zone ${state.zone}`;
      if (frost) {
        if (state.frost && state.frost.frostFree) frost.textContent = 'Frost uncommon';
        else if (state.frost && state.frost.lastFrost) frost.textContent = formatFrostDate(state.frost.lastFrost);
        else frost.textContent = '—';
      }
    });
    updateMapMarker(state);
    updateMapPicks();
  }

  function updateHero(state) {
    $$('[data-ac-home-hero]').forEach((hero) => {
      const eyebrow = $('[data-ac-hero-eyebrow]', hero);
      const label = $('[data-ac-hero-zone-label]', hero);
      const value = $('[data-ac-hero-zone-value]', hero);
      const meta = $('[data-ac-hero-zone-meta]', hero);

      if (!state) return;

      const phase = plantingPhase(state);
      hero.dataset.zoneState = 'known';

      if (eyebrow) eyebrow.textContent = 'Your growing zone';
      if (label) label.textContent = displayLocation(state);
      if (value) value.textContent = `Zone ${state.zone} · ${phase.label}`;

      if (meta) {
        if (state.frost && !state.frost.frostFree && state.frost.lastFrost && state.frost.firstFrost) {
          meta.textContent = `Last frost ${formatFrostDate(state.frost.lastFrost)} · First frost ${formatFrostDate(state.frost.firstFrost)}`;
          meta.hidden = false;
        } else if (state.frost && state.frost.frostFree) {
          meta.textContent = 'Typical 32°F frost is uncommon at the nearest climate station';
          meta.hidden = false;
        } else {
          meta.hidden = true;
          meta.textContent = '';
        }
      }
    });
  }

  async function loadZoneProducts(state) {
    const major = majorZone(state && state.zone);
    if (!major) return;

    const sections = $$('[data-ac-popular-picks]');
    if (!sections.length) return;

    const handle = `zone-${major}`;
    const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
    const requestUrl = `${root}collections/${handle}?sort_by=best-selling&section_id=ac-zone-product-grid`;

    if (activeProductRequest && activeProductRequest.abort) {
      activeProductRequest.abort();
    }

    const controller = new AbortController();
    activeProductRequest = controller;

    sections.forEach((section) => {
      const grid = $('[data-ac-picks-grid]', section);
      if (grid) grid.setAttribute('aria-busy', 'true');
    });

    try {
      const response = await fetch(requestUrl, {
        credentials: 'same-origin',
        signal: controller.signal
      });

      if (!response.ok) throw new Error('Zone collection unavailable.');
      const html = await response.text();

      const doc = new DOMParser().parseFromString(html, 'text/html');
      const sourceItems = doc.querySelector('.ac-zone-product-grid__items');
      if (!sourceItems || !sourceItems.children.length) return;

      sections.forEach((section) => {
        const grid = $('[data-ac-picks-grid]', section);
        const eyebrow = $('[data-ac-picks-eyebrow]', section);
        const heading = $('[data-ac-picks-heading]', section);
        const viewAll = $('[data-ac-picks-view-all]', section);
        const viewAllLabel = $('[data-ac-picks-view-all-label]', section);

        if (grid) grid.innerHTML = sourceItems.innerHTML;
        if (eyebrow) eyebrow.textContent = section.dataset.knownEyebrow || 'Curated for your growing zone';
        if (heading) heading.textContent = `${section.dataset.knownHeading || 'Popular Picks for Zone'} ${state.zone}`;
        if (viewAll) viewAll.href = `${root}collections/${handle}`;
        if (viewAllLabel) viewAllLabel.textContent = section.dataset.knownViewAll || 'View All Zone Seeds';
      });
      updateMapPicks();
    } catch (error) {
      if (error.name !== 'AbortError') {
        console.warn('[AC Zone] Keeping Best Sellers fallback:', error);
      }
    } finally {
      sections.forEach((section) => {
        const grid = $('[data-ac-picks-grid]', section);
        if (grid) grid.removeAttribute('aria-busy');
      });
    }
  }

  function applyZone(state, { fetchProducts = true } = {}) {
    if (!state) return;

    updateHero(state);
    setFinderMessage(state);
    updateMapCard(state);

    $$('[data-ac-popular-picks]').forEach((section) => {
      const eyebrow = $('[data-ac-picks-eyebrow]', section);
      const heading = $('[data-ac-picks-heading]', section);
      if (eyebrow) eyebrow.textContent = section.dataset.knownEyebrow || 'Curated for your growing zone';
      if (heading) heading.textContent = `${section.dataset.knownHeading || 'Popular Picks for Zone'} ${state.zone}`;
    });

    if (fetchProducts) loadZoneProducts(state);

    document.dispatchEvent(new CustomEvent('ac:zonechange', { detail: state }));
  }

  async function resolveZip(rawZip, options = {}) {
    const zip = normalizeZip(rawZip);
    if (!zip) throw new Error('INVALID_ZIP');

    if (resolvingZip === zip) return;
    resolvingZip = zip;

    try {
      const lookup = await loadLookup();
      const zone = lookup[zip];
      if (!zone) throw new Error('ZONE_NOT_FOUND');

      const finder = $('[data-ac-zone-finder]');
      const customerZip = finder ? normalizeZip(finder.dataset.acCustomerZip) : '';
      const customerMatches = customerZip && customerZip === zip;

      let state = {
        zip,
        zone,
        major: majorZone(zone),
        version: 'USDA-PHZM-2023',
        source: options.source || 'manual',
        city: customerMatches ? (finder.dataset.acCustomerCity || '') : '',
        state: customerMatches ? (finder.dataset.acCustomerState || '') : ''
      };

      // Apply the local USDA result immediately; frost enrichment should never block the page.
      saveZone(state);
      applyZone(state);

      const frost = await loadFrostData(zip);
      if (frost) {
        state = { ...state, frost };
        saveZone(state);
        applyZone(state, { fetchProducts: false });
      }

      return state;
    } finally {
      resolvingZip = null;
    }
  }

  function setSubmitLoading(submit, isLoading) {
    if (!submit) return;

    if (isLoading) {
      submit.disabled = true;
      submit.dataset.originalText = submit.textContent;
      submit.textContent = 'Finding…';
    } else {
      submit.disabled = false;
      submit.textContent = submit.dataset.originalText || 'Find My Zone';
    }
  }

  function initForms() {
    $$('[data-ac-zone-form]').forEach((form) => {
      if (form.dataset.acZoneBound === 'true') return;
      form.dataset.acZoneBound = 'true';

      form.addEventListener('submit', async (event) => {
        event.preventDefault();

        const finder = form.closest('[data-ac-zone-finder]');
        const input = $('[data-ac-zone-zip]', form);
        const submit = $('[data-ac-zone-submit]', form);
        const zip = normalizeZip(input && input.value);

        if (!zip) {
          showFinderError(finder, 'Please enter a valid 5-digit U.S. ZIP code.');
          if (input) input.focus();
          return;
        }

        setSubmitLoading(submit, true);

        try {
          await resolveZip(zip, { source: 'manual' });
        } catch (error) {
          const message = error.message === 'ZONE_NOT_FOUND'
            ? 'We could not find a 2023 USDA hardiness zone for that ZIP code.'
            : 'Zone lookup is temporarily unavailable. Please try again.';
          showFinderError(finder, message);
          console.error('[AC Zone]', error);
        } finally {
          setSubmitLoading(submit, false);
        }
      });
    });
  }

  function initChangeZip() {
    $$('[data-ac-change-zip]').forEach((button) => {
      if (button.dataset.acZoneBound === 'true') return;
      button.dataset.acZoneBound = 'true';

      button.addEventListener('click', () => {
        clearSavedZone();

        const finder = button.closest('[data-ac-zone-finder]');
        const result = $('[data-ac-zone-result]', finder);
        const input = $('[data-ac-zone-zip]', finder);

        if (result) result.hidden = true;
        if (input) {
          input.value = '';
          input.focus();
        }
      });
    });
  }

  function initManualZoneLinks() {
    $$('[data-ac-manual-zone]').forEach((link) => {
      if (link.dataset.acZoneBound === 'true') return;
      link.dataset.acZoneBound = 'true';

      link.addEventListener('click', () => {
        const zone = link.dataset.acManualZone;
        if (!zone) return;

        const state = {
          zip: '',
          zone,
          major: majorZone(zone),
          version: 'manual',
          source: 'manual-zone'
        };

        saveZone(state);
      });
    });
  }

  async function initPersonalization() {
    const saved = getSavedZone();

    // 1) A visitor's explicit prior ZIP/zone always wins over account address.
    if (saved) {
      applyZone(saved);

      // Upgrade an older saved state with frost data once, without blocking zone UI.
      if (saved.zip && !saved.frost) {
        try {
          const frost = await loadFrostData(saved.zip);
          if (frost) {
            const enriched = { ...saved, frost };
            saveZone(enriched);
            applyZone(enriched, { fetchProducts: false });
          }
        } catch (e) {}
      }

      return;
    }

    // 2) If logged in, Shopify exposes the default address.
    // We only use a US 5-digit ZIP and never overwrite a visitor-chosen ZIP.
    const finder = $('[data-ac-zone-finder]');
    const customerZip = finder ? normalizeZip(finder.dataset.acCustomerZip) : '';

    if (customerZip) {
      try {
        await resolveZip(customerZip, { source: 'shopify-customer-default-address' });
      } catch (error) {
        console.warn('[AC Zone] Customer ZIP could not be resolved; keeping generic fallback.', error);
      }
    }

    // 3) Otherwise the page remains generic until the visitor enters ZIP.
  }

  function init() {
    initForms();
    initChangeZip();
    initManualZoneLinks();
    initPersonalization();
  }

  document.addEventListener('DOMContentLoaded', init);
  document.addEventListener('shopify:section:load', init);
})();
